// src/pages/RecursosHub.jsx
// Hub de recursos en /recursos
// Grid de tarjetas — cada módulo tiene su propia página de detalle

import { Link } from 'react-router-dom';
import { ArrowRight, FlaskConical, Brain, Handshake, Scale } from 'lucide-react';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';

const MODULES = [
  {
    slug: '/simulador-b2b',
    badge: 'Simulador IA',
    icon: Handshake,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-700',
    accentBorder: 'hover:border-blue-300',
    accentBtn: 'bg-[#1E3A8A] hover:bg-[#17316F]',
    title: 'Sparring B2B — Negociación con IA',
    description:
      'Practica una conversación B2B con múltiples decisores. El cliente no está alineado, finanzas duda del ROI y tecnología teme la integración. Tu reto es destrabar la decisión sin presionar ni regalar valor.',
    tags: ['Ventas consultivas', 'Gestión de stakeholders', 'Cierre de deals'],
    cta: 'Practicar ahora',
  },
  {
    slug: '/recursos/lean-simulador',
    badge: 'Metodología Lean',
    icon: FlaskConical,
    iconBg: 'bg-cyan-100',
    iconColor: 'text-cyan-700',
    accentBorder: 'hover:border-cyan-300',
    accentBtn: 'bg-cyan-600 hover:bg-cyan-700',
    title: 'Simulador Lean Startup — Validación de hipótesis',
    description:
      'Entrena cómo pensar antes de construir. Define hipótesis, detecta supuestos débiles y diseña experimentos mínimos para validar si tu idea merece avanzar.',
    tags: ['Problem-solution fit', 'Experimentos MVP', 'Métricas de validación'],
    cta: 'Validar una idea',
  },
  {
    slug: '/recursos/agentic-ai',
    badge: 'Arquitectura IA',
    icon: Brain,
    iconBg: 'bg-violet-100',
    iconColor: 'text-violet-700',
    accentBorder: 'hover:border-violet-300',
    accentBtn: 'bg-violet-600 hover:bg-violet-700',
    title: 'Cómo opera un sistema Agentic AI',
    description:
      'Demo visual paso a paso: observa cómo un agente interpreta contexto, consulta conocimiento, usa herramientas y ejecuta acciones reales.',
    tags: ['Memoria contextual', 'RAG', 'Orquestación'],
    cta: 'Ver la demo',
  },
  {
    slug: '/recursos/simulador-decision',
    badge: 'Decision Lab · Beta',
    icon: Scale,
    iconBg: 'bg-slate-900',
    iconColor: 'text-teal-400',
    accentBorder: 'hover:border-slate-500',
    accentBtn: 'bg-slate-800 hover:bg-slate-700',
    title: 'Simulador de Decisión Ejecutiva',
    description:
      'Tres escenarios reales, sin respuesta correcta. Descubre si el Sistema 1 te captura, en qué nivel sistémico intervenes y cuánto ruido hay en tu criterio decisional.',
    tags: ['Sistema 1 vs Sistema 2', 'Pensamiento sistémico', 'Ruido decisional'],
    cta: 'Descubrir mi perfil',
  },
];

export default function RecursosHub() {
  return (
    <>
      <SEOHead
        title="Recursos Gratuitos — Simuladores e IA | Edutech Pulse Lab"
        description="3 herramientas gratuitas: Simulador B2B de negociación con IA, Simulador Lean Startup para validar ideas, y demo de arquitectura Agentic AI. Sin registro."
        keywords="simulador negociación B2B, ventas consultivas IA, simulador lean startup, agentic AI demo, herramientas startup gratis, recursos educativos IA, negociación con inteligencia artificial"
        canonicalUrl="/recursos"
        ogType="website"
        image="https://edutechpulselab.com/og-recursos.png"
        schema={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Recursos Gratuitos — Simuladores e IA",
          "description": "Herramientas gratuitas de Edutech Pulse Lab: simulador B2B de negociación, simulador Lean Startup y demo de Agentic AI.",
          "url": "https://edutechpulselab.com/recursos",
          "provider": {
            "@type": "Organization",
            "name": "Edutech Pulse Lab",
            "url": "https://edutechpulselab.com"
          },
          "hasPart": [
            {
              "@type": "WebApplication",
              "name": "Sparring B2B — Negociación con IA",
              "url": "https://edutechpulselab.com/simulador-b2b",
              "applicationCategory": "EducationalApplication",
              "isAccessibleForFree": true,
              "description": "Practica una negociación B2B real contra un comité comprador simulado por IA. Sin registro."
            },
            {
              "@type": "WebApplication",
              "name": "Simulador para Validar tu Startup",
              "url": "https://edutechpulselab.com/recursos/lean-simulador",
              "applicationCategory": "EducationalApplication",
              "isAccessibleForFree": true,
              "description": "Flujo guiado para validar hipótesis críticas basado en Lean Startup y Running Lean."
            },
            {
              "@type": "WebApplication",
              "name": "Cómo opera un sistema Agentic AI",
              "url": "https://edutechpulselab.com/recursos/agentic-ai",
              "applicationCategory": "EducationalApplication",
              "isAccessibleForFree": true,
              "description": "Demo visual paso a paso de arquitectura Agentic AI: memoria, RAG y orquestación."
            }
          ]
        }}
      />

      <Navbar />

      <main className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">

        {/* ── Header ──────────────────────────────────────────────── */}
        <section className="bg-white border-b border-slate-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-16">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3 sm:mb-4">
              Herramientas gratuitas
            </p>
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-2 sm:mb-4">
              Simuladores y herramientas de práctica
            </h1>
            <p className="text-base sm:text-xl text-slate-600 leading-relaxed max-w-2xl">
              Experiencias gratuitas para entrenar criterio, validar decisiones y practicar conversaciones reales con IA.
            </p>
          </div>
        </section>

        {/* ── Grid de tarjetas ────────────────────────────────────── */}
        <section className="max-w-5xl mx-auto px-4 sm:px-8 py-6 sm:py-16">
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-8">
            {MODULES.map((mod) => (
              <article
                key={mod.slug}
                className={`
                  group bg-white rounded-2xl border border-slate-200
                  transition-all duration-200
                  hover:shadow-lg ${mod.accentBorder}
                  flex flex-col overflow-hidden
                `}
              >
                <div className="p-5 sm:p-7 md:p-8 flex flex-col flex-1 gap-4 sm:gap-5">

                  {/* Icono + badge */}
                  <div className="flex items-start justify-between">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${mod.iconBg} flex items-center justify-center`}>
                      <mod.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${mod.iconColor}`} />
                    </div>
                    <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2.5 sm:px-3 py-1 rounded-full">
                      {mod.badge}
                    </span>
                  </div>

                  {/* Título */}
                  <h2 className="text-xl font-bold text-slate-900 leading-snug">
                    {mod.title}
                  </h2>

                  {/* Descripción */}
                  <p className="text-sm text-slate-600 leading-relaxed flex-1">
                    {mod.description}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2">
                    {mod.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* CTA */}
                  <Link
                    to={mod.slug}
                    className={`
                      inline-flex items-center gap-2 px-5 py-2.5
                      ${mod.accentBtn} text-white font-semibold
                      rounded-lg transition-colors text-sm self-start
                    `}
                  >
                    {mod.cta}
                    <ArrowRight className="w-4 h-4" />
                  </Link>

                </div>
              </article>
            ))}
          </div>
        </section>

      </main>
    </>
  );
}
